/**
 * Document Helper Utility
 * This utility provides direct document manipulation functions
 * that don't rely on the main service layer
 */

/**
 * Directly update a document by calling createNewVersion method
 * This bypasses the service layer to access the backend API directly
 *
 * @param {string} documentId - Document ID to update
 * @param {string} content - New document content
 * @returns {Promise<Object>} Updated document data
 */
export const directUpdateDocument = async (documentId, content) => {
  try {
    console.log(`Direct update document ${documentId} with content length: ${content.length}`);

    // Get auth token from localStorage
    const token = localStorage.getItem('token');

    // Prepare request data for the createNewVersion endpoint
    const requestData = { content, method: 'createNewVersion' };
    const url = `/api/documents/${documentId}`;

    console.log(`Making direct PUT request to ${url} (createNewVersion method)`);

    // Use the fetch API directly
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Request-Method': 'createNewVersion' // Add a header to help identify the method
      },
      body: JSON.stringify(requestData)
    });

    // Check for response status
    if (!response.ok) {
      throw new Error(`Server returned ${response.status} ${response.statusText}`);
    }

    // Parse the response
    const responseData = await response.json();
    console.log('Document update response:', responseData);

    // Check for success flag
    if (!responseData.success) {
      throw new Error(responseData.message || 'Server returned error status');
    }

    return responseData.data;
  } catch (error) {
    console.error('Error in directUpdateDocument utility:', error);

    // Create a helpful error message
    let errorMessage = 'Failed to update document: ';
    errorMessage += error.message || 'Unknown error';

    throw new Error(errorMessage);
  }
};

/**
 * Download document content as a markdown file
 * 
 * @param {string} content - Document content
 * @param {string} fileName - File name without extension
 */
export const downloadAsMarkdown = (content, fileName = 'document') => {
  try {
    // Create a blob with the content
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.md`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading markdown:', error);
    throw new Error(`Failed to download document: ${error.message}`);
  }
};

/**
 * Save document locally in browser storage
 * This can be used as a fallback when server saving fails
 * 
 * @param {string} documentId - Document ID
 * @param {string} content - Document content
 */
export const saveDocumentLocally = (documentId, content) => {
  try {
    const key = `doc_backup_${documentId}`;
    const data = {
      content,
      timestamp: new Date().toISOString(),
      id: documentId
    };
    
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving document locally:', error);
    return false;
  }
};

/**
 * Get locally saved document
 * 
 * @param {string} documentId - Document ID
 * @returns {Object|null} Saved document data or null
 */
export const getLocalDocument = (documentId) => {
  try {
    const key = `doc_backup_${documentId}`;
    const data = localStorage.getItem(key);
    
    if (!data) return null;
    
    return JSON.parse(data);
  } catch (error) {
    console.error('Error getting local document:', error);
    return null;
  }
};